import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserCheck,
  Coins,
  Image as ImageIcon,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit3,
  X,
  Plus,
  Minus,
  Shield,
  ArrowLeft,
  ShieldCheck,
  ShieldOff,
  Banknote,
  CheckCircle,
  AlertCircle,
  Eye,
  RotateCcw,
  Trash2,
  FolderOpen,
  ToggleLeft,
  Save,
  Link2,
  History,
  Settings,
  Megaphone,
  PlayCircle,
  Plug,
  MessageCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { extractFunctionError } from '@/lib/functionError';
import { useAuth } from '@/context/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';
import { AdminUserStats } from '@/types';
import { ToolSettingsView } from './ToolSettingsView';
import { AdminTryoutTokensView } from './AdminTryoutTokensView';
import { AdminCostView } from './AdminCostView';
import { AdminUpdatesView } from './AdminUpdatesView';
import { AdminTutorialsView } from './AdminTutorialsView';
import { AdminSupportView } from './AdminSupportView';
import { fetchAdminUnreadCount } from '@/services/supportService';
import { AdminIntegrationsView } from './AdminIntegrationsView';
import { ImageHistoryDrawer } from './ImageHistoryDrawer';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface AdminDashboardProps {
  onBack: () => void;
}

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'indigo' | 'green' | 'amber' | 'rose';
  isLoading?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, color, isLoading }) => {
  const colorClasses = {
    indigo: 'bg-primary/10 text-primary',
    green: 'bg-emerald-500/10 text-emerald-500',
    amber: 'bg-amber-500/10 text-amber-500',
    rose: 'bg-rose-500/10 text-rose-500',
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground">{value}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface CreditEditModalProps {
  user: AdminUserStats;
  onClose: () => void;
  onSave: (userId: string, amount: number, floorplanAmount: number, reason: string) => Promise<void>;
}

const CreditEditModal: React.FC<CreditEditModalProps> = ({ user, onClose, onSave }) => {
  const [amount, setAmount] = useState(0);
  const [floorplanAmount, setFloorplanAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (amount === 0 && floorplanAmount <= 0) {
      setError('Betrag darf nicht 0 sein');
      return;
    }
    if (!reason.trim()) {
      setError('Bitte gib einen Grund an');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(user.user_id, amount, floorplanAmount, reason);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const newCredits = user.credits + amount;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Credits anpassen</DialogTitle>
          <DialogDescription>
            Ändere das Guthaben für {user.email}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-sm font-medium text-muted-foreground">User</Label>
            <p className="font-medium text-foreground">{user.email}</p>
            <p className="text-sm text-muted-foreground">
              Aktuelles Guthaben: <span className="font-bold text-primary">{user.credits} Credits</span>
            </p>
          </div>

          <div className="space-y-2">
            <Label>Änderung</Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setAmount(a => a - 10)}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                className="text-center w-24 font-bold"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setAmount(a => a + 10)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Neues Guthaben:{' '}
              <span className={`font-bold ${newCredits < 0 ? 'text-destructive' : 'text-emerald-500'}`}>
                {newCredits} Credits
              </span>
            </p>
          </div>

          <div className="space-y-2">
            <Label>Grundriss-Credits (nur Vergabe)</Label>
            <Input
              type="number"
              min={0}
              value={floorplanAmount}
              onChange={(e) => { const n = parseInt(e.target.value || '0', 10); setFloorplanAmount(Number.isNaN(n) ? 0 : Math.max(0, n)); }}
              className="w-24 font-bold"
            />
            <p className="text-xs text-muted-foreground">
              Zusätzliche Grundriss-Einheiten vergeben (nur positive Werte, kein Entzug möglich).
            </p>
          </div>

          <div className="space-y-2">
            <Label>Grund *</Label>
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Bonus, Erstattung, Support..."
            />
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || (amount === 0 && floorplanAmount <= 0) || !reason.trim() || newCredits < 0}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Toggle Switch Component
const AdminToggle: React.FC<{
  checked: boolean;
  disabled: boolean;
  loading: boolean;
  onChange: () => void;
}> = ({ checked, disabled, loading, onChange }) => {
  return (
    <button
      onClick={onChange}
      disabled={disabled || loading}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${checked ? 'bg-primary' : 'bg-muted-foreground/30'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title={disabled ? 'Kann eigenen Admin-Status nicht ändern' : (checked ? 'Admin-Rechte entziehen' : 'Zum Admin ernennen')}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
      {loading && (
        <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />
      )}
    </button>
  );
};

const ITEMS_PER_PAGE = 10;

type AdminTab = 'users' | 'payouts' | 'tool-settings' | 'features' | 'tryout' | 'costs' | 'updates' | 'tutorials' | 'support' | 'integrations';

const TOGGLEABLE_FEATURES = [
  { key: 'overlay_tool', label: 'Overlay Tool', description: 'Manuelles Overlay-Positionierungstool' },
  { key: 'overlay_builder', label: 'Overlay Builder', description: 'Overlay-Vorlagen erstellen' },
  { key: 'affiliate', label: 'Partnerprogramm', description: 'Affiliate-Dashboard und Verwaltung' },
  { key: 'floorplan', label: 'Grundriss-Tool', description: 'AI-Grundrissoptimierung: 2D/3D-Generierung und Korrektur' },
  { key: 'admin_prompt_testing', label: 'Admin Prompt-Testing', description: 'Prompt-Editor und QA-Analyse in der Review-Ansicht (nur für Admins)' },
];

// Ein kleiner Toggle mit Label darüber (für die Admin/Live-Doppelschalter).
const LabeledToggle: React.FC<{ label: string; on: boolean; disabled?: boolean; onClick: () => void }> = ({ label, on, disabled, onClick }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    <button
      type="button"
      onClick={() => { if (!disabled) onClick(); }}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${on ? 'bg-primary' : 'bg-muted-foreground/30'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

// Boolean-Feature-Flags. Jedes Feature hat je einen Admin- und einen Live-Schalter
// (konsumiert als `isAdmin ? _admin : _live`).
const BOOLEAN_FEATURES: Array<{
  label: string;
  description: string;
  adminKey: 'catalog_staging_admin' | 'stage_refinement_admin' | 'floorplan_consult_admin' | 'floorplan_fixture_symbols_admin' | 'flow_v3_admin' | 'floorplan_refine_v2_admin' | 'empty_consult_admin' | 'support_chat_admin';
  liveKey: 'catalog_staging_live' | 'stage_refinement_live' | 'floorplan_consult_live' | 'floorplan_fixture_symbols_live' | 'flow_v3_live' | 'floorplan_refine_v2_live' | 'empty_consult_live' | 'support_chat_live';
}> = [
  { label: 'Katalog-Staging (v2)', description: 'Echte Möbel aus Moodboards + 2-Phasen-Flow (erst Leer bestätigen, dann einrichten). Aus = klassisches Staging.', adminKey: 'catalog_staging_admin', liveKey: 'catalog_staging_live' },
  { label: 'Staging-Refinement (Box-Editor)', description: 'Box-Mask-Edit-Mode im Staging-Tool. Aus-Schalten = sofortiger Notfall-Rollback.', adminKey: 'stage_refinement_admin', liveKey: 'stage_refinement_live' },
  { label: 'Grundriss-Analyst (Bad/Küche)', description: 'Vision-Analyst beschreibt Bad & Küche dynamisch fürs 2D-Prompt (statt starrem Block).', adminKey: 'floorplan_consult_admin', liveKey: 'floorplan_consult_live' },
  { label: 'Sanitär-Vektorsymbole', description: 'Sanitär-Einbauten als S/W-Vektorsymbole ins Bild brennen (statt farbiger Boxen), mit Rotation. Aus = farbige Boxen wie bisher.', adminKey: 'floorplan_fixture_symbols_admin', liveKey: 'floorplan_fixture_symbols_live' },
  { label: 'Flow v3 (Assistent + Workspace)', description: 'Neuer Projekt-Flow: Empty-first-Assistent (Hochladen → Leeren → Einrichten → Overlay) + Workspace mit Tabs statt Detailseite. Aus = klassischer Flow.', adminKey: 'flow_v3_admin', liveKey: 'flow_v3_live' },
  { label: 'Grundriss-Reprompting v2 (Nano Banana)', description: 'Grundriss-Feintuning per Nano-Banana-Ganzbild statt gpt-image-2+Maske: strukturelle Edits mit Original als Referenz, präzise Lokalisierung. Aus = alter Masken-Pfad.', adminKey: 'floorplan_refine_v2_admin', liveKey: 'floorplan_refine_v2_live' },
  { label: 'Leerräum-Berater', description: 'Vision-Analyst schaut das Foto an und formuliert das Leerräumen-Prompt bildspezifisch aus (Objekte benennen, Nebenräume, saubere Wandflächen). Aus = statisches Standard-Prompt.', adminKey: 'empty_consult_admin', liveKey: 'empty_consult_live' },
  { label: 'Support-Chat', description: 'Ersetzt die Termin-Karte des Hilfe-Buttons durch den Support-Chat (asynchrone Anfragen). Aus = bisherige Calendly-Karte.', adminKey: 'support_chat_admin', liveKey: 'support_chat_live' },
];

interface PendingPayout {
  affiliateUserId: string;
  email: string;
  fullName: string | null;
  totalPendingCents: number;
  earningsCount: number;
  iban: string | null;
  accountHolder: string | null;
}

interface AdminProject {
  id: string;
  name: string;
  aspect_ratio: string;
  created_at: string;
  image_count: number;
}

interface AdminImage {
  id: string;
  storage_path: string | null;
  storage_path_staged: string | null;
  storage_path_empty: string | null;
  storage_path_floorplan_2d: string | null;
  storage_path_floorplan_3d: string | null;
  room_type: string | null;
  status: string | null;
  created_at: string;
}

interface ViewingUser {
  userId: string;
  email: string;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onBack }) => {
  const { user: currentUser, disabledFeatures, refreshFeatureFlags } = useAuth();
  const { settings: appSettings } = useAppSettings();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Support: ungelesene User-Nachrichten (Badge am Tab)
  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    fetchAdminUnreadCount().then(setSupportUnread).catch(() => {});
  }, [activeTab]); // beim Tab-Wechsel aktualisieren (openTicket markiert gelesen)

  // Realtime: Badge bumpt live bei neuen User-Nachrichten (Publication: Migration 122).
  // Event ist nur der Trigger — gezählt wird über den Count-Query (Quelle der Wahrheit).
  useEffect(() => {
    const channel = supabase
      .channel('admin-support-unread')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages' },
        (payload) => {
          if ((payload.new as { sender_role: string }).sender_role !== 'user') return;
          fetchAdminUnreadCount().then(setSupportUnread).catch(() => {});
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, []);

  // Feature flags state
  const [localDisabled, setLocalDisabled] = useState<string[]>([]);
  const [isSavingFeatures, setIsSavingFeatures] = useState(false);
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>({});
  
  const [kpis, setKpis] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalCreditsUsed: 0,
    totalImagesGenerated: 0,
  });
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);

  const [users, setUsers] = useState<AdminUserStats[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState<'registered' | 'last_login' | 'credits'>('registered');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [payingFilter, setPayingFilter] = useState<'all' | 'paying' | 'non_paying'>('all');

  const [editingUser, setEditingUser] = useState<AdminUserStats | null>(null);
  const [togglingAdminId, setTogglingAdminId] = useState<string | null>(null);

  // Payouts state
  const [payouts, setPayouts] = useState<PendingPayout[]>([]);
  const [isLoadingPayouts, setIsLoadingPayouts] = useState(true);
  const [processingPayoutId, setProcessingPayoutId] = useState<string | null>(null);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [payoutSuccess, setPayoutSuccess] = useState<string | null>(null);

  // Project browser state
  const [viewingUser, setViewingUser] = useState<ViewingUser | null>(null);
  const [userProjects, setUserProjects] = useState<AdminProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [viewingProject, setViewingProject] = useState<AdminProject | null>(null);
  const [projectImages, setProjectImages] = useState<AdminImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [viewingHistoryImageId, setViewingHistoryImageId] = useState<string | null>(null);
  const [viewingHistoryRoomType, setViewingHistoryRoomType] = useState<string | null>(null);

  // Reset onboarding state
  const [resetOnboardingUser, setResetOnboardingUser] = useState<AdminUserStats | null>(null);
  const [isResettingOnboarding, setIsResettingOnboarding] = useState(false);

  // Delete user state
  const [deletingUser, setDeletingUser] = useState<AdminUserStats | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const fetchKPIs = useCallback(async () => {
    setIsLoadingKpis(true);
    try {
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Try secure RPC function first, fallback to view if not deployed yet
      let activeData = null;
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_admin_user_stats', { p_limit: 1000, p_offset: 0 });
      
      if (rpcError) {
        // Fallback to view (will be removed after migration is deployed)
        const { data: viewData } = await supabase
          .from('admin_user_stats')
          .select('user_id, last_sign_in_at')
          .gte('last_sign_in_at', thirtyDaysAgo.toISOString());
        activeData = viewData;
      } else {
        activeData = rpcData?.filter((u: { last_sign_in_at: string | null }) => 
          u.last_sign_in_at && new Date(u.last_sign_in_at) >= thirtyDaysAgo
        );
      }

      const { data: creditData } = await supabase.from('credit_usage').select('credits_used');
      const totalImages = creditData?.reduce((sum, row) => sum + (row.credits_used || 0), 0) || 0;

      const { data: txData } = await supabase
        .from('transactions')
        .select('amount')
        .eq('status', 'completed');
      const totalCreditsUsed = txData?.reduce((sum, row) => sum + (row.amount || 0), 0) || 0;

      setKpis({
        totalUsers: userCount || 0,
        activeUsers: activeData?.length || 0,
        totalCreditsUsed,
        totalImagesGenerated: totalImages,
      });
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    } finally {
      setIsLoadingKpis(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;

      // Try secure RPC function first
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_admin_user_stats', {
        p_search: searchQuery.trim() || null,
        p_limit: ITEMS_PER_PAGE,
        p_offset: offset,
        p_sort_by: sortBy,
        p_sort_dir: sortDir,
        p_paying: payingFilter === 'all' ? null : payingFilter === 'paying',
      });

      console.log('[AdminDashboard] RPC response:', { rpcData, rpcError });

      if (rpcError) {
        console.error('[AdminDashboard] RPC error:', rpcError.message, rpcError);
        throw rpcError;
      }

      if (rpcData && rpcData.length > 0) {
        const totalCount = rpcData[0]?.total_count || rpcData.length;
        setUsers(rpcData);
        setTotalUsers(totalCount);
      } else {
        // No data returned
        console.log('[AdminDashboard] No users returned from RPC');
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [currentPage, searchQuery, sortBy, sortDir, payingFilter]);

  const fetchPayouts = useCallback(async () => {
    setIsLoadingPayouts(true);
    try {
      // Fetch pending earnings grouped by user with profile and affiliate_settings data
      const { data: earningsData, error: earningsError } = await supabase
        .from('affiliate_earnings')
        .select(`
          affiliate_user_id,
          amount_in_cents
        `)
        .eq('status', 'pending');

      if (earningsError) throw earningsError;

      // Group by affiliate_user_id
      const groupedEarnings = new Map<string, { total: number; count: number }>();
      for (const earning of earningsData || []) {
        const existing = groupedEarnings.get(earning.affiliate_user_id) || { total: 0, count: 0 };
        existing.total += earning.amount_in_cents;
        existing.count += 1;
        groupedEarnings.set(earning.affiliate_user_id, existing);
      }

      // Filter for >= 5000 cents (50€) and fetch user details
      const eligibleUserIds = Array.from(groupedEarnings.entries())
        .filter(([, data]) => data.total >= 5000)
        .map(([userId]) => userId);

      if (eligibleUserIds.length === 0) {
        setPayouts([]);
        setIsLoadingPayouts(false);
        return;
      }

      // Fetch profiles
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', eligibleUserIds);

      // Fetch affiliate settings
      const { data: settingsData } = await supabase
        .from('affiliate_settings')
        .select('user_id, iban, account_holder')
        .in('user_id', eligibleUserIds);

      // Build payout objects
      const payoutList: PendingPayout[] = eligibleUserIds.map(userId => {
        const earnings = groupedEarnings.get(userId)!;
        const profile = profilesData?.find(p => p.id === userId);
        const settings = settingsData?.find(s => s.user_id === userId);

        return {
          affiliateUserId: userId,
          email: profile?.email || 'Unbekannt',
          fullName: profile?.full_name || null,
          totalPendingCents: earnings.total,
          earningsCount: earnings.count,
          iban: settings?.iban || null,
          accountHolder: settings?.account_holder || null,
        };
      });

      // Sort by total descending
      payoutList.sort((a, b) => b.totalPendingCents - a.totalPendingCents);

      setPayouts(payoutList);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setIsLoadingPayouts(false);
    }
  }, []);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === 'payouts') {
      fetchPayouts();
    }
  }, [activeTab, fetchPayouts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Sync local feature flags state with global state
  useEffect(() => {
    setLocalDisabled([...disabledFeatures]);
  }, [disabledFeatures]);

  useEffect(() => {
    setLocalFlags({
      catalog_staging_admin: appSettings.catalog_staging_admin,
      catalog_staging_live: appSettings.catalog_staging_live,
      stage_refinement_admin: appSettings.stage_refinement_admin,
      stage_refinement_live: appSettings.stage_refinement_live,
      floorplan_consult_admin: appSettings.floorplan_consult_admin,
      floorplan_consult_live: appSettings.floorplan_consult_live,
      floorplan_fixture_symbols_admin: appSettings.floorplan_fixture_symbols_admin,
      floorplan_fixture_symbols_live: appSettings.floorplan_fixture_symbols_live,
      flow_v3_admin: appSettings.flow_v3_admin,
      flow_v3_live: appSettings.flow_v3_live,
      floorplan_refine_v2_admin: appSettings.floorplan_refine_v2_admin,
      floorplan_refine_v2_live: appSettings.floorplan_refine_v2_live,
      empty_consult_admin: appSettings.empty_consult_admin,
      empty_consult_live: appSettings.empty_consult_live,
      support_chat_admin: appSettings.support_chat_admin,
      support_chat_live: appSettings.support_chat_live,
    });
  }, [appSettings.catalog_staging_admin, appSettings.catalog_staging_live, appSettings.stage_refinement_admin, appSettings.stage_refinement_live, appSettings.floorplan_consult_admin, appSettings.floorplan_consult_live, appSettings.floorplan_fixture_symbols_admin, appSettings.floorplan_fixture_symbols_live, appSettings.flow_v3_admin, appSettings.flow_v3_live, appSettings.floorplan_refine_v2_admin, appSettings.floorplan_refine_v2_live, appSettings.empty_consult_admin, appSettings.empty_consult_live, appSettings.support_chat_admin, appSettings.support_chat_live]);

  const handleSaveFeatures = async () => {
    setIsSavingFeatures(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ disabled_features: localDisabled, ...localFlags, updated_at: new Date().toISOString() })
        .eq('id', 1);
      if (error) throw error;
      await refreshFeatureFlags();
    } catch (err) {
      console.error('Failed to save feature flags:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSavingFeatures(false);
    }
  };

  const handleCreditUpdate = async (userId: string, amount: number, floorplanAmount: number, reason: string) => {
    const { data: { session } } = await supabase.auth.getSession();

    const { data, error } = await supabase.functions.invoke('manage-user-credits', {
      body: { userId, amount, floorplanAmount, reason },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    });

    if (error) throw new Error(await extractFunctionError(error, 'Credits konnten nicht aktualisiert werden'));
    if (!data.success) throw new Error(data.error);

    await fetchUsers();
    await fetchKPIs();
  };

  const handleMarkPayout = async (payout: PendingPayout) => {
    if (!confirm(`Wirklich ${(payout.totalPendingCents / 100).toFixed(2)}€ für ${payout.email} als bezahlt markieren?`)) {
      return;
    }

    setProcessingPayoutId(payout.affiliateUserId);
    setPayoutError(null);
    setPayoutSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('admin-action', {
        body: {
          targetUserId: payout.affiliateUserId,
          action: 'mark_payout',
          headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
        },
      });

      if (error) throw new Error(await extractFunctionError(error, 'Auszahlung konnte nicht markiert werden'));
      if (!data.success) throw new Error(data.error);

      setPayoutSuccess(data.message);
      setTimeout(() => setPayoutSuccess(null), 5000);

      // Refresh payouts
      await fetchPayouts();
    } catch (err) {
      console.error('Failed to mark payout:', err);
      setPayoutError(err instanceof Error ? err.message : 'Fehler beim Markieren der Auszahlung');
    } finally {
      setProcessingPayoutId(null);
    }
  };

  const handleAdminToggle = async (targetUser: AdminUserStats) => {
    // Prevent self-demotion
    if (targetUser.user_id === currentUser?.id) {
      return;
    }

    setTogglingAdminId(targetUser.user_id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('admin-action', {
        body: {
          targetUserId: targetUser.user_id,
          action: 'toggle_admin',
          value: !targetUser.is_admin,
        },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });

      if (error) throw new Error(await extractFunctionError(error, 'Admin-Status konnte nicht geändert werden'));
      if (!data.success) throw new Error(data.error);

      // Optimistic update
      setUsers(prev => prev.map(u => 
        u.user_id === targetUser.user_id 
          ? { ...u, is_admin: !u.is_admin }
          : u
      ));
    } catch (err) {
      console.error('Failed to toggle admin:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Ändern des Admin-Status');
    } finally {
      setTogglingAdminId(null);
    }
  };

  // ========================================================================
  // Reset Onboarding Handler
  // ========================================================================
  const handleResetOnboarding = async (targetUser: AdminUserStats) => {
    setIsResettingOnboarding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke('admin-action', {
        body: {
          targetUserId: targetUser.user_id,
          action: 'reset_onboarding',
          value: true,
        },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });

      if (error) throw new Error(await extractFunctionError(error, 'Onboarding konnte nicht zurückgesetzt werden'));
      if (!data.success) throw new Error(data.error);

      setResetOnboardingUser(null);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to reset onboarding:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Zurücksetzen');
    } finally {
      setIsResettingOnboarding(false);
    }
  };

  // ========================================================================
  // Delete User Handler
  // ========================================================================
  const handleDeleteUser = async (targetUser: AdminUserStats) => {
    setIsDeletingUser(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke('admin-action', {
        body: {
          targetUserId: targetUser.user_id,
          action: 'delete_user',
          value: true,
        },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });

      if (error) throw new Error(await extractFunctionError(error, 'Nutzer konnte nicht gelöscht werden'));
      if (!data.success) throw new Error(data.error);

      setDeletingUser(null);
      setConfirmEmail('');
      await fetchUsers();
      await fetchKPIs();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setIsDeletingUser(false);
    }
  };

  // ========================================================================
  // View User Projects Handler
  // ========================================================================
  const handleViewUserProjects = async (targetUser: AdminUserStats) => {
    setViewingUser({ userId: targetUser.user_id, email: targetUser.email || 'Unbekannt' });
    setViewingProject(null);
    setIsLoadingProjects(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_user_projects', {
        p_user_id: targetUser.user_id,
      });

      if (error) throw error;
      setUserProjects(data || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setUserProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleViewProjectImages = async (project: AdminProject) => {
    setViewingProject(project);
    setIsLoadingImages(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_project_images', {
        p_project_id: project.id,
      });

      if (error) throw error;
      setProjectImages(data || []);
    } catch (err) {
      console.error('Failed to load images:', err);
      setProjectImages([]);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const getImageUrl = (path: string | null): string | null => {
    if (!path) return null;
    const { data } = supabase.storage.from('images').getPublicUrl(path);
    return data.publicUrl;
  };

  const totalPages = Math.ceil(totalUsers / ITEMS_PER_PAGE);

  const toggleSort = (col: 'registered' | 'last_login' | 'credits') => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };
  const sortArrow = (col: 'registered' | 'last_login' | 'credits') =>
    sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleOpenList = useCallback((listId: string) => {
    window.history.pushState({}, '', `/admin/tryout-lists/${listId}`);
    window.location.href = `/admin/tryout-lists/${listId}`;
  }, []);

  return (
    <div className="animate-fade-in">
      {editingUser && (
        <CreditEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleCreditUpdate}
        />
      )}

      {/* Reset Onboarding Dialog */}
      {resetOnboardingUser && (
        <Dialog open={true} onOpenChange={(open) => !open && setResetOnboardingUser(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Onboarding zurücksetzen</DialogTitle>
              <DialogDescription>
                Das Onboarding für <span className="font-medium text-foreground">{resetOnboardingUser.email}</span> wird zurückgesetzt.
                Der User muss das Onboarding beim nächsten Login erneut durchlaufen.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetOnboardingUser(null)} disabled={isResettingOnboarding}>
                Abbrechen
              </Button>
              <Button onClick={() => handleResetOnboarding(resetOnboardingUser)} disabled={isResettingOnboarding}>
                {isResettingOnboarding ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zurücksetzen...</>
                ) : (
                  <><RotateCcw className="w-4 h-4 mr-2" /> Zurücksetzen</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete User Dialog */}
      {deletingUser && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) { setDeletingUser(null); setConfirmEmail(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">Account löschen</DialogTitle>
              <DialogDescription>
                Der Account <span className="font-medium text-foreground">{deletingUser.email}</span> wird unwiderruflich gelöscht.
                Alle Daten (Projekte, Bilder, Transaktionen) gehen verloren.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
                Tippe <span className="font-mono font-bold">{deletingUser.email}</span> ein, um die Löschung zu bestätigen.
              </div>
              <Input
                type="text"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Email-Adresse eingeben..."
                className="font-mono"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeletingUser(null); setConfirmEmail(''); }} disabled={isDeletingUser}>
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDeleteUser(deletingUser)}
                disabled={isDeletingUser || confirmEmail !== deletingUser.email}
              >
                {isDeletingUser ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Löschen...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> Endgültig löschen</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onBack} size="icon">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          </div>
          <p className="text-muted-foreground mt-1">User-Verwaltung und Statistiken</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="Gesamte User"
          value={kpis.totalUsers}
          icon={<Users className="w-6 h-6" />}
          color="indigo"
          isLoading={isLoadingKpis}
        />
        <KPICard
          title="Aktive User (30 Tage)"
          value={kpis.activeUsers}
          icon={<UserCheck className="w-6 h-6" />}
          color="green"
          isLoading={isLoadingKpis}
        />
        <KPICard
          title="Verkaufte Credits"
          value={kpis.totalCreditsUsed}
          icon={<Coins className="w-6 h-6" />}
          color="amber"
          isLoading={isLoadingKpis}
        />
        <KPICard
          title="Generierte Bilder"
          value={kpis.totalImagesGenerated}
          icon={<ImageIcon className="w-6 h-6" />}
          color="rose"
          isLoading={isLoadingKpis}
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <nav className="flex gap-1">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Users className="w-4 h-4" />
            User-Übersicht
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'payouts'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Banknote className="w-4 h-4" />
            Auszahlungen
            {payouts.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-amber-500/10 text-amber-500 text-xs rounded-full">
                {payouts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'features'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <ToggleLeft className="w-4 h-4" />
            Features
          </button>
          <button
            onClick={() => setActiveTab('tool-settings')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tool-settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Settings className="w-4 h-4" />
            Tool-Einstellungen
          </button>
          <button
            onClick={() => setActiveTab('tryout')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tryout'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Link2 className="w-4 h-4" />
            Tryout-Links
          </button>
          <button
            onClick={() => setActiveTab('costs')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'costs'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Banknote className="w-4 h-4" />
            Kosten & Marge
          </button>
          <button
            onClick={() => setActiveTab('updates')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'updates'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Megaphone className="w-4 h-4" />
            Updates
          </button>
          <button
            onClick={() => setActiveTab('tutorials')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tutorials'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <PlayCircle className="w-4 h-4" />
            Tutorials
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'support'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Support
            {supportUnread > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full">
                {supportUnread}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'integrations'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
            }`}
          >
            <Plug className="w-4 h-4" />
            Integrationen
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-border">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <h2 className="text-lg font-bold text-foreground">User-Übersicht</h2>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex gap-1 rounded-lg border border-border p-0.5">
                  {(
                    [
                      ['all', 'Alle'],
                      ['paying', 'Zahlend'],
                      ['non_paying', 'Nicht zahlend'],
                    ] as const
                  ).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => {
                        setPayingFilter(val);
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${
                        payingFilter === val
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Suchen nach Email oder Name..."
                    className="pl-10 w-full sm:w-64"
                  />
                </div>
              </div>
            </div>
          </div>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery ? 'Keine User gefunden' : 'Noch keine User vorhanden'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted text-left text-sm text-muted-foreground">
                      <th className="px-4 py-3 font-medium">User</th>
                      <th className="px-4 py-3 font-medium">
                        <button
                          type="button"
                          onClick={() => toggleSort('credits')}
                          className="inline-flex items-center hover:text-foreground"
                        >
                          Credits{sortArrow('credits')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Grundriss</th>
                      <th className="px-4 py-3 font-medium">Admin</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Bilder</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Projekte</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">
                        <button
                          type="button"
                          onClick={() => toggleSort('registered')}
                          className="inline-flex items-center hover:text-foreground"
                        >
                          Registriert{sortArrow('registered')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">
                        <button
                          type="button"
                          onClick={() => toggleSort('last_login')}
                          className="inline-flex items-center hover:text-foreground"
                        >
                          Letzter Login{sortArrow('last_login')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((user) => (
                      <tr key={user.user_id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                              {(user.email?.[0] || '?').toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">
                                {user.email || 'Unbekannt'}
                                {user.total_spent_cents > 0 && (
                                  <span className="ml-2 inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium px-1.5 py-0.5 align-middle">
                                    Zahlend
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">{user.full_name || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-primary">{user.credits}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                          {user.floorplan_singles + user.floorplan_quota_month}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <AdminToggle
                              checked={user.is_admin}
                              disabled={user.user_id === currentUser?.id}
                              loading={togglingAdminId === user.user_id}
                              onChange={() => handleAdminToggle(user)}
                            />
                            {user.is_admin ? (
                              <ShieldCheck className="w-4 h-4 text-primary" />
                            ) : (
                              <ShieldOff className="w-4 h-4 text-muted-foreground/50" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                          {user.total_images_generated}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">
                          {user.project_count}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-sm">
                          {user.registered_at
                            ? new Date(user.registered_at).toLocaleDateString('de-DE')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-sm">
                          {user.last_sign_in_at
                            ? new Date(user.last_sign_in_at).toLocaleDateString('de-DE')
                            : 'Nie'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingUser(user)}
                              className="hover:text-primary h-8 w-8"
                              title="Credits bearbeiten"
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewUserProjects(user)}
                              className="hover:text-primary h-8 w-8"
                              title="Projekte ansehen"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setResetOnboardingUser(user)}
                              className="hover:text-amber-500 h-8 w-8"
                              title="Onboarding zurücksetzen"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            {user.user_id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeletingUser(user)}
                                className="hover:text-destructive h-8 w-8"
                                title="Account löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Zeige {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, totalUsers)} von {totalUsers}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Seite {currentPage} von {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Project Browser Panel */}
      {viewingUser && activeTab === 'users' && (
        <Card className="mt-6">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (viewingProject) {
                      setViewingProject(null);
                      setProjectImages([]);
                    } else {
                      setViewingUser(null);
                      setUserProjects([]);
                    }
                  }}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h3 className="font-bold text-foreground">
                    {viewingProject
                      ? viewingProject.name
                      : `Projekte von ${viewingUser.email}`}
                  </h3>
                  {viewingProject && (
                    <p className="text-sm text-muted-foreground">
                      {viewingUser.email} — {viewingProject.aspect_ratio || 'Kein Format'}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setViewingUser(null);
                  setViewingProject(null);
                  setUserProjects([]);
                  setProjectImages([]);
                }}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Project List */}
            {!viewingProject && (
              <>
                {isLoadingProjects ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                ) : userProjects.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p>Keine Projekte vorhanden</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    {userProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleViewProjectImages(project)}
                        className="text-left p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <FolderOpen className="w-5 h-5 text-primary" />
                          <span className="font-medium text-foreground truncate">{project.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{project.image_count} Bilder</span>
                          <span>{project.aspect_ratio || '-'}</span>
                          <span>{new Date(project.created_at).toLocaleDateString('de-DE')}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Image Grid */}
            {viewingProject && (
              <>
                {isLoadingImages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                ) : projectImages.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p>Keine Bilder in diesem Projekt</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                    {projectImages.map((image) => {
                      const originalUrl = getImageUrl(image.storage_path);
                      const stagedUrl = getImageUrl(image.storage_path_staged);
                      const emptyUrl = getImageUrl(image.storage_path_empty);
                      const floorplan2dUrl = getImageUrl(image.storage_path_floorplan_2d);
                      const floorplan3dUrl = getImageUrl(image.storage_path_floorplan_3d);
                      const isFloorplan = !!(floorplan2dUrl || floorplan3dUrl);
                      return (
                        <div key={image.id} className="space-y-2">
                          {originalUrl && (
                            <a href={originalUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={originalUrl}
                                alt="Original"
                                className="w-full aspect-[4/3] object-cover rounded-lg border border-border hover:border-primary/50 transition-colors"
                              />
                            </a>
                          )}
                          {stagedUrl && (
                            <a href={stagedUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={stagedUrl}
                                alt="Staged"
                                className="w-full aspect-[4/3] object-cover rounded-lg border border-emerald-500/30 hover:border-emerald-500 transition-colors"
                              />
                            </a>
                          )}
                          {emptyUrl && (
                            <a href={emptyUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={emptyUrl}
                                alt="Leer"
                                className="w-full aspect-[4/3] object-cover rounded-lg border border-amber-500/30 hover:border-amber-500 transition-colors"
                              />
                            </a>
                          )}
                          {floorplan2dUrl && (
                            <a href={floorplan2dUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={floorplan2dUrl}
                                alt="Grundriss 2D"
                                className="w-full aspect-square object-cover rounded-lg border border-blue-500/30 hover:border-blue-500 transition-colors"
                              />
                              <span className="text-[10px] text-blue-600 font-medium">Grundriss 2D</span>
                            </a>
                          )}
                          {floorplan3dUrl && (
                            <a href={floorplan3dUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={floorplan3dUrl}
                                alt="Grundriss 3D"
                                className="w-full aspect-square object-cover rounded-lg border border-violet-500/30 hover:border-violet-500 transition-colors"
                              />
                              <span className="text-[10px] text-violet-600 font-medium">Grundriss 3D</span>
                            </a>
                          )}
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div>
                              <p>{isFloorplan ? 'Grundriss' : (image.room_type || 'Kein Raumtyp')} — {image.status || '-'}</p>
                              <p>{new Date(image.created_at).toLocaleDateString('de-DE')}</p>
                            </div>
                            <button
                              onClick={() => {
                                setViewingHistoryImageId(image.id);
                                setViewingHistoryRoomType(image.room_type || null);
                              }}
                              className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                              title="Historie anzeigen"
                            >
                              <History className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <ToggleLeft className="w-5 h-5 text-primary" />
                Feature Flags
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Zentrale Verwaltung aller Feature-Flags.
              </p>
            </div>

            <div className="px-4 py-2.5 bg-muted/40 border-b border-border">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sidebar-Sichtbarkeit</p>
              <p className="text-xs text-muted-foreground/80 mt-0.5">Deaktivierte Features werden für Nicht-Admins aus der Sidebar ausgeblendet.</p>
            </div>
            <div className="divide-y divide-border">
              {TOGGLEABLE_FEATURES.map((feature) => {
                const isDisabled = localDisabled.includes(feature.key);
                return (
                  <div key={feature.key} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium text-foreground">{feature.label}</p>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                    <button
                      onClick={() => {
                        setLocalDisabled(prev =>
                          prev.includes(feature.key)
                            ? prev.filter(k => k !== feature.key)
                            : [...prev, feature.key]
                        );
                      }}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer
                        ${!isDisabled ? 'bg-primary' : 'bg-muted-foreground/30'}
                      `}
                      title={isDisabled ? 'Feature aktivieren' : 'Feature deaktivieren'}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${!isDisabled ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-2.5 bg-muted/40 border-y border-border">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Feature-Schalter</p>
              <p className="text-xs text-muted-foreground/80 mt-0.5">Funktions-Flags (unabhängig von der Sidebar). Eingerückte Untereinträge gelten nur, wenn das übergeordnete Feature an ist.</p>
            </div>
            <div className="divide-y divide-border">
              {BOOLEAN_FEATURES.map((f) => {
                // Admin- + Live-Doppelschalter.
                return (
                  <div key={f.adminKey} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium text-foreground">{f.label}</p>
                      <p className="text-sm text-muted-foreground">{f.description}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <LabeledToggle
                        label="Admin"
                        on={!!localFlags[f.adminKey!]}
                        onClick={() => setLocalFlags((prev) => ({ ...prev, [f.adminKey!]: !prev[f.adminKey!] }))}
                      />
                      <LabeledToggle
                        label="Live"
                        on={!!localFlags[f.liveKey!]}
                        onClick={() => setLocalFlags((prev) => ({ ...prev, [f.liveKey!]: !prev[f.liveKey!] }))}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-border flex justify-end">
              <Button
                onClick={handleSaveFeatures}
                disabled={isSavingFeatures || (
                  JSON.stringify([...localDisabled].sort()) === JSON.stringify([...disabledFeatures].sort())
                  && localFlags.catalog_staging_admin === appSettings.catalog_staging_admin
                  && localFlags.catalog_staging_live === appSettings.catalog_staging_live
                  && localFlags.stage_refinement_admin === appSettings.stage_refinement_admin
                  && localFlags.stage_refinement_live === appSettings.stage_refinement_live
                  && localFlags.floorplan_consult_admin === appSettings.floorplan_consult_admin
                  && localFlags.floorplan_consult_live === appSettings.floorplan_consult_live
                  && localFlags.floorplan_fixture_symbols_admin === appSettings.floorplan_fixture_symbols_admin
                  && localFlags.floorplan_fixture_symbols_live === appSettings.floorplan_fixture_symbols_live
                  && localFlags.flow_v3_admin === appSettings.flow_v3_admin
                  && localFlags.flow_v3_live === appSettings.flow_v3_live
                  && localFlags.floorplan_refine_v2_admin === appSettings.floorplan_refine_v2_admin
                  && localFlags.floorplan_refine_v2_live === appSettings.floorplan_refine_v2_live
                  && localFlags.empty_consult_admin === appSettings.empty_consult_admin
                  && localFlags.empty_consult_live === appSettings.empty_consult_live
                  && localFlags.support_chat_admin === appSettings.support_chat_admin
                  && localFlags.support_chat_live === appSettings.support_chat_live
                )}
              >
                {isSavingFeatures ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Speichern</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool Settings Tab */}
      {activeTab === 'tool-settings' && <ToolSettingsView />}

      {/* Tryout Links Tab */}
      {activeTab === 'tryout' && (
        <AdminTryoutTokensView onOpenList={handleOpenList} />
      )}

      {/* Costs Tab */}
      {activeTab === 'costs' && <AdminCostView />}

      {/* Updates Tab */}
      {activeTab === 'updates' && <AdminUpdatesView />}

      {/* Tutorials Tab */}
      {activeTab === 'tutorials' && <AdminTutorialsView />}

      {/* Support Tab */}
      {activeTab === 'support' && <AdminSupportView />}

      {activeTab === 'integrations' && <AdminIntegrationsView />}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Banknote className="w-5 h-5 text-primary" />
                Offene Auszahlungen
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Affiliates mit mindestens 50€ offener Provision
              </p>
            </div>

            {/* Success/Error Messages */}
            {payoutSuccess && (
              <div className="m-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-emerald-600">{payoutSuccess}</span>
              </div>
            )}
            {payoutError && (
              <div className="m-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-destructive" />
                <span className="text-sm text-destructive">{payoutError}</span>
                <button onClick={() => setPayoutError(null)} className="ml-auto">
                  <X className="w-4 h-4 text-destructive/70" />
                </button>
              </div>
            )}

            {isLoadingPayouts ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : payouts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Banknote className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                <p>Keine offenen Auszahlungen</p>
                <p className="text-sm mt-1">Alle Affiliates haben weniger als 50€ offene Provision.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted text-left text-sm text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Affiliate</th>
                      <th className="px-4 py-3 font-medium">Offener Betrag</th>
                      <th className="px-4 py-3 font-medium">Provisionen</th>
                      <th className="px-4 py-3 font-medium">IBAN</th>
                      <th className="px-4 py-3 font-medium">Kontoinhaber</th>
                      <th className="px-4 py-3 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payouts.map((payout) => (
                      <tr key={payout.affiliateUserId} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 font-bold">
                              {(payout.email?.[0] || '?').toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{payout.email}</p>
                              <p className="text-sm text-muted-foreground">{payout.fullName || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-lg text-emerald-500">
                            {(payout.totalPendingCents / 100).toFixed(2)}€
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {payout.earningsCount} Provision{payout.earningsCount !== 1 ? 'en' : ''}
                        </td>
                        <td className="px-4 py-3">
                          {payout.iban ? (
                            <span className="font-mono text-sm text-foreground">
                              {payout.iban.replace(/(.{4})/g, '$1 ').trim()}
                            </span>
                          ) : (
                            <span className="text-destructive text-sm flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Nicht hinterlegt
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {payout.accountHolder || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleMarkPayout(payout)}
                            disabled={processingPayoutId === payout.affiliateUserId || !payout.iban}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                              !payout.iban
                                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                                : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500'
                            }`}
                            title={!payout.iban ? 'Keine Bankdaten hinterlegt' : 'Als bezahlt markieren'}
                          >
                            {processingPayoutId === payout.affiliateUserId ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Verarbeite...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                Als bezahlt markieren
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ImageHistoryDrawer
        imageId={viewingHistoryImageId}
        roomType={viewingHistoryRoomType}
        onClose={() => { setViewingHistoryImageId(null); setViewingHistoryRoomType(null); }}
      />
    </div>
  );
};
